import { useMemo } from "react";
import {
  CancellationPolicyEnum,
  HotelConfirmationCodeEnum,
  type Reservation,
} from "@b2bportal/lodging-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { htmlDecode } from "@hopper-b2b/utilities";
import { formatGuestSummary } from "../../utils/formatGuests";

// The hotel's own confirmation number (matches the confirmation email), not
// the internal reservationBookingId. Only available once the hotel confirms.
export const getHotelReference = (
  reservation: Pick<Reservation, "hotelConfirmationCode">
): string => {
  const hotelConfirmationCode = reservation.hotelConfirmationCode;
  return hotelConfirmationCode?.HotelConfirmationCode ===
    HotelConfirmationCodeEnum.HotelConfirmationConfirmed
    ? hotelConfirmationCode.confirmationCode
    : "";
};

export const useBookingDetails = (reservation: Reservation) => {
  const { t } = useI18nContext();

  return useMemo(() => {
    const primaryGuest = reservation.guests?.[0];
    const primaryGuestName = primaryGuest
      ? [primaryGuest.givenName, primaryGuest.surname].filter(Boolean).join(" ")
      : "";

    const adultCount = reservation.adultCount ?? 0;
    const childCount = reservation.childCount ?? 0;
    const childrenAges = reservation.childrenAges ?? [];
    const guestSummary = formatGuestSummary(
      t,
      adultCount,
      childCount,
      childrenAges
    );

    const roomInfo = reservation.bookedRooms?.[0]?.roomInfo;
    const roomCount = reservation.bookedRooms?.[0]?.count ?? 1;
    const rawRoomName = roomInfo?.name ?? reservation.roomInfo?.name ?? "";
    const roomName = htmlDecode(rawRoomName) ?? rawRoomName;
    const roomLabel =
      roomCount > 1
        ? `${roomName} (${t("hotelTrips.room", { count: roomCount })})`
        : roomName;

    const cancellationPolicy = reservation.cancellationPolicy;
    const isRefundable =
      cancellationPolicy?.CancellationPolicy ===
      CancellationPolicyEnum.Refundable;

    return {
      confirmationCode: reservation.reservationId,
      hotelReference: getHotelReference(reservation),
      primaryGuestName,
      guestSummary,
      email: reservation.bookingEmail ?? "",
      roomLabel,
      isRefundable,
      cancellationPrimaryText: cancellationPolicy?.primaryText ?? "",
    };
  }, [reservation, t]);
};
